import { useEffect, useRef, useState } from 'preact/hooks';

interface GestureHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onTap?: () => void;
  onDoubleTap?: () => void;
  onLongPress?: () => void;
  onPinch?: (scale: number) => void;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

export function useGestures<T extends HTMLElement = HTMLDivElement>(handlers: GestureHandlers) {
  const elementRef = useRef<T>(null);
  const touchStart = useRef<TouchPoint | null>(null);
  const lastTap = useRef<number>(0);
  const longPressTimer = useRef<number | null>(null);
  const [isPressing, setIsPressing] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const SWIPE_THRESHOLD = 50;
    const TAP_THRESHOLD = 10;
    const DOUBLE_TAP_DELAY = 300;
    const LONG_PRESS_DELAY = 500;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      setIsPressing(true);

      // Start long press timer
      if (handlers.onLongPress) {
        longPressTimer.current = window.setTimeout(() => {
          handlers.onLongPress?.();
          touchStart.current = null; // Prevent tap after long press
        }, LONG_PRESS_DELAY);
      }
    };

    const handleTouchMove = (_e: TouchEvent) => {
      // Cancel long press if user moves
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      setIsPressing(false);
      
      // Clear long press timer
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      if (!touchStart.current) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.current.x;
      const deltaY = touch.clientY - touchStart.current.y;
      const deltaTime = Date.now() - touchStart.current.time;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Check for tap
      if (distance < TAP_THRESHOLD && deltaTime < 300) {
        const now = Date.now();
        const timeSinceLastTap = now - lastTap.current;

        if (timeSinceLastTap < DOUBLE_TAP_DELAY) {
          // Double tap
          handlers.onDoubleTap?.();
          lastTap.current = 0;
        } else {
          // Single tap
          handlers.onTap?.();
          lastTap.current = now;
        }
      } 
      // Check for swipe
      else if (distance > SWIPE_THRESHOLD) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Horizontal swipe
          if (deltaX > 0) {
            handlers.onSwipeRight?.();
          } else {
            handlers.onSwipeLeft?.();
          }
        } else {
          // Vertical swipe
          if (deltaY > 0) {
            handlers.onSwipeDown?.();
          } else {
            handlers.onSwipeUp?.();
          }
        }
      }

      touchStart.current = null;
    };

    // Pinch gesture for zoom
    let initialDistance = 0;
    
    const handleTouchStartPinch = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        initialDistance = Math.sqrt(dx * dx + dy * dy);
      }
    };

    const handleTouchMovePinch = (e: TouchEvent) => {
      if (e.touches.length === 2 && initialDistance > 0) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const scale = distance / initialDistance;
        handlers.onPinch?.(scale);
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    if (handlers.onPinch) {
      element.addEventListener('touchstart', handleTouchStartPinch, { passive: true });
      element.addEventListener('touchmove', handleTouchMovePinch, { passive: true });
    }

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchstart', handleTouchStartPinch);
      element.removeEventListener('touchmove', handleTouchMovePinch);
      
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, [handlers]);

  return {
    ref: elementRef,
    isPressing,
  };
}